import { Button, Spinner } from 'components/common';
import { Perk } from './types';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useState } from 'react';
import notification from 'lib/notification';
import { pluralize } from 'utils';
import { formatValidityDate, isExpired } from './utils';
import { PERK_TYPES } from './constants';

interface PerkProgressButtonProps {
  perk: Perk;
  onUnlock?: (perk: Perk) => Promise<void>;
  onRedeemClick?: (perk: Perk) => void;
  isApplicable?: boolean;
  onApplyCoupon?: (perk: Perk, couponCode?: string) => Promise<void>;
  appliedCouponCode?: string | null;
}

export const PerkProgressButton = ({
  perk,
  onUnlock,
  onRedeemClick,
  isApplicable,
  onApplyCoupon,
  appliedCouponCode,
}: PerkProgressButtonProps) => {
  const {
    quota,
    total_redeem,
    progress,
    trigger,
    type,
    scanning,
  } = perk;
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const isScanning = scanning && scanning?.status === 'processing';

  if (!isScanning && !progress) return null;

  const handleUnlock = async (perk: Perk) => {
    try {
      setIsUnlocking(true);
      await onUnlock?.(perk);
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.message ||
        error.message ||
        'Something went wrong. Please try again.';
      notification.error(errorMessage);
    } finally {
      setIsUnlocking(false);
    }
  };

  const isFullyRedeemed = quota === total_redeem;
  const isUnlockedByUser = Boolean(progress?.unlocked_reward);
  const isRedeemed = Boolean(progress?.unlocked_reward?.redeemed_at);
  const isEligibleToUnlock =
    progress?.is_unlockable || progress?.step === trigger?.target;
  const isRewardExpired = isExpired(
    progress?.unlocked_reward?.expired_at,
  );

  const getRemainingProgress = () => {
    const remaining =
      Number(trigger.target) - Number(progress?.step ?? 0);
    return Number.isNaN(remaining) ? 0 : remaining;
  };

  const getRemainingProgressText = () => {
    const remaining = getRemainingProgress();
    if (trigger.type === 'order-spend') {
      return (
        <span className="tw-font-semibold">
          Spend{' '}
          <span className="tw-font-therebrat tw-font-normal tw-text-4xl">
            ${remaining.toFixed(2)}
          </span>{' '}
          more to unlock!
        </span>
      );
    } else if (trigger.type === 'total-order') {
      return (
        <span className="tw-font-semibold">
          Make{' '}
          <span className="tw-font-therebrat tw-font-normal tw-text-4xl">
            {remaining}
          </span>{' '}
          more {pluralize(remaining, 'order')} to unlock!
        </span>
      );
    }

    return null;
  };

  if (isScanning) {
    return (
      <Button
        className="tw-w-full tw-pointer-events-none"
        loading
        disabled
      >
        <span className="tw-flex tw-items-center tw-justify-center tw-gap-2">
          <span className="tw-font-medium tw-text-2xl tw-py-0.5">
            Calculating your progress...
          </span>
        </span>
      </Button>
    );
  }

  if (isUnlockedByUser) {
    if (isRedeemed) {
      return (
        <Button disabled className="tw-w-full tw-pointer-events-none">
          <span className="tw-font-bold tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-2xl tw-py-[0.625rem] tw-leading-none">
            <span>Already Redeemed!</span>
          </span>
        </Button>
      );
    }

    if (isRewardExpired) {
      return (
        <Button disabled className="tw-w-full tw-pointer-events-none">
          <span className="tw-font-bold tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-2xl tw-py-[0.625rem] tw-leading-none">
            <span>Oops, this Perk has expired 😢</span>
          </span>
        </Button>
      );
    }

    if (type === PERK_TYPES.PARTNER_MANUAL) {
      return (
        <Button
          onClick={() => onRedeemClick?.(perk)}
          className="tw-w-full"
        >
          <span className="tw-font-bold tw-flex tw-items-center tw-justify-center tw-gap-2">
            <span className="tw-text-4xl tw-font-therebrat">
              Click Here
            </span>
            <span>to Redeem In-Store</span>
          </span>
        </Button>
      );
    }

    return (
      <div className="tw-flex tw-flex-col tw-space-y-3">
        <CopyToClipboard
          text={progress?.unlocked_reward?.reward.code}
          onCopy={async () => {
            if (isApplicable) {
              if (
                appliedCouponCode ===
                progress?.unlocked_reward?.reward.code
              )
                return;

              try {
                setIsApplyingCoupon(true);
                await onApplyCoupon?.(
                  perk,
                  progress?.unlocked_reward?.reward.code,
                );
                setCopied(true);
              } catch (err) {
                notification.error(
                  err?.errors?.message ??
                    'Something went wrong when applying coupon.',
                );
                setCopied(false);
              } finally {
                setIsApplyingCoupon(false);
              }
            } else {
              setCopied(true);
            }

            setTimeout(() => {
              setCopied(false);
            }, 1500);
          }}
        >
          <Button
            color="light"
            className={`tw-text-primary tw-w-full ${
              isApplicable
                ? 'tw-border-dashed tw-border tw-border-white-200 tw-rounded-lg'
                : ''
            }`}
            disabled={isApplyingCoupon}
          >
            {copied ? (
              <>
                {isApplicable ? (
                  <span className="tw-font-bold tw-text-3xl tw-py-0.5">
                    Applied
                  </span>
                ) : (
                  <span className="tw-font-bold tw-text-3xl tw-py-0.5">
                    Copied!
                  </span>
                )}
              </>
            ) : (
              <div className="tw-flex tw-flex-col tw-w-full">
                <div className="tw-flex tw-items-center tw-justify-center">
                  <span className="tw-font-bold tw-text-3xl tw-py-0.5 tw-whitespace-nowrap tw-overflow-hidden tw-max-w-[calc(100%-1rem)] tw-text-ellipsis">
                    {progress?.unlocked_reward?.reward.code}
                  </span>
                  <i className="far fa-clone" />
                </div>
                {isApplicable && (
                  <>
                    {isApplyingCoupon ? (
                      <div className="tw-flex tw-w-full tw-items-center tw-justify-center">
                        <Spinner size="sm" />
                      </div>
                    ) : (
                      <span>
                        {appliedCouponCode ===
                        progress?.unlocked_reward?.reward.code ? (
                          'Applied'
                        ) : (
                          <>
                            <span className="tw-hidden md:tw-inline-block">
                              Click
                            </span>
                            <span className="tw-inline-block md:tw-hidden">
                              Tap
                            </span>{' '}
                            to copy & apply
                          </>
                        )}
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
          </Button>
        </CopyToClipboard>
        {!isRewardExpired && (
          <div className="tw-text-center tw-italic tw-text-[12px] text-muted tw-leading-none">
            {progress?.unlocked_reward?.expired_at === null
              ? 'No expiry'
              : `Use by ${formatValidityDate(
                  progress?.unlocked_reward?.expired_at ?? null,
                )}`}
          </div>
        )}
      </div>
    );
  }

  if (isFullyRedeemed) {
    return (
      <Button disabled color="light" className="tw-w-full">
        <span className="tw-font-bold tw-flex tw-items-center tw-justify-center tw-gap-2">
          It's{' '}
          <span className="tw-font-therebrat tw-text-4xl">Fully</span>{' '}
          redeemed!
        </span>
      </Button>
    );
  }

  if (isEligibleToUnlock) {
    return (
      <Button
        onClick={async () => await handleUnlock(perk)}
        className="tw-w-full"
        loading={isUnlocking}
        disabled={isUnlocking}
      >
        <span className="tw-flex tw-items-center tw-justify-center tw-gap-2">
          <span className="tw-text-4xl tw-font-therebrat">100%</span>
          <span className="tw-font-bold">- Unlock Now!</span>
        </span>
      </Button>
    );
  }

  const progressPercentage =
    progress && trigger?.target
      ? Math.min(
          Math.round((progress.step / trigger.target) * 100),
          100,
        )
      : 0;

  return (
    <div
      className="tw-relative tw-w-full tw-py-[0.85rem] tw-px-6 tw-bg-[#f8f9fa] tw-overflow-hidden"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="tw-absolute tw-inset-0 tw-bg-[#fec902] tw-transition-all tw-duration-300"
        style={{ width: `${progressPercentage.toFixed(2)}%` }}
      />
      <div
        className={`tw-text-center tw-block tw-font-semibold tw-text-black tw-transition-transform tw-duration-300 tw-transform tw-relative ${
          hovered
            ? '-tw-translate-y-full tw-opacity-0'
            : 'tw-translate-y-0 tw-opacity-100'
        }`}
      >
        You're{' '}
        <span className="tw-text-4xl tw-font-therebrat tw-font-normal tw-mx-1 tw-tracking-wider">
          {progressPercentage}%
        </span>
        there!
      </div>
      <div
        className={`tw-absolute tw-inset-0 tw-pointer-events-none tw-flex tw-items-center tw-justify-center tw-font-semibold tw-text-black tw-transition-transform tw-duration-300 tw-transform ${
          hovered
            ? 'tw-translate-y-0 tw-opacity-100'
            : 'tw-translate-y-full tw-opacity-0'
        }`}
      >
        {getRemainingProgressText()}
      </div>
    </div>
  );
};
